import { Link, useOutletContext, useLocation } from "react-router-dom";
import {
  Box,
  List,
  Typography,
  IconButton,
  Button,
  Snackbar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import React from "react";
import moment from "moment-timezone";

// Components
import PageAbstract from "components/PageAbstract";
import ImageError from "components/ImageError";
import AvatarIcon from "../../components/AvatarIcon";
import IconsAccounts from "../../components/IconsAccounts";
import TagInput from "../../components/TagAutoComplete/TagInput";
import SuccessAlert from "../../components/SuccessAlert";
import ErrorAlert from "../../components/ErrorAlert";
import ConfirmDialog from "../../components/ConfirmDialog";
import LinearDeterminate from "../../components/LinearDeterminate";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Close } from "@mui/icons-material";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

// Local components
import AccountSelectionDialog from "./components/AccountSelectionDialog";
import MediaUploadDialog from "./components/MediaUploadDialog";
import DraftCollectionPopper from "./components/DraftCollectionPopper";
import SocialPreview from "./components/SocialPreview";
import DateScheduler from "./components/DateScheduler";
import AIGenerateDialog from "./components/AIGenerateDialog";
import YouTubeSettings from "./components/YouTubeSettings";
import TelegramSettings from "./components/TelegramSettings";
import InstagramSettings from "./components/InstagramSettings";
import TikTokSettings from "./components/TikTokSettings";
import PublishStatusDialog from "./components/PublishStatusDialog";
import ThreadsThreadEditor from "./components/ThreadsThreadEditor";

// Images - used in subcomponents
// import { ReactComponent as EmptyAcc } from "../../images/No accounts.svg";

// Utils and constants
import { DEFAULT_STATE, CONTENT_TYPES, ACTIVE_DATE_TYPES, SOCIAL_TYPES } from "./constants";
import {
  formatScheduleDate,
  formatTagsArray,
  getMinToDate,
  isTrialEnded,
  loadDraftData,
  // toggleArrayItem
} from "./utils";
import {
  getEffectiveField,
  getEffectiveData,
  hasOverride,
  buildPostGroups,
  buildTypeDataForGroup,
} from "./overrides";
import {
  validateAccount,
  issuesStatus,
  formatIssue,
  getTextCounterForAccount,
  SEVERITY,
} from "./platformRules";
import { formatTagsForText } from "utils/threadsText";

// Actions
import _helper from "actions/_helper";
import _request from "actions/_request";
import { THUMB_SIZE } from "actions/_request/Files";
import i18n from "i18";

// Styles
import "./style.css";
import { IOSSwitch } from "./styles";

class PostPage extends PageAbstract {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      ...DEFAULT_STATE,
      activeTab: 0,
      aiDialogOpen: false,
      // Telegram-specific state
      telegramPinMessage: false,
      telegramPinSilent: false,
      telegramInlineKeyboard: [[]],
      // Instagram-specific state
      instagramPostType: "feed",
      // TikTok Creator Info per account: { [accountId]: { data, loading, error } }
      tiktokCreatorInfoByAccount: {},
      // Per-account overrides: { [accountId]: { field: value, ... } }
      accountOverrides: {},
      // Currently edited scope: null = base (all accounts), or accountId
      activeOverrideAccountId: null,
      // Open when user tries to publish a post that has only warnings.
      confirmPublishOpen: false,
      // Publish status dialog (opens after a successful newPost fan-out).
      publishStatusOpen: false,
      publishStatusPostIds: [],
    };

    // Bind methods
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleClickOpen = this.handleClickOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleCloseImageDialog = this.handleCloseImageDialog.bind(this);
  }

  postId = () => {
    const url = new URLSearchParams(window.location.search);
    const userId = url.get("id");

    if (userId) {
      this.setState({ postID: userId });
    }
  };

  async loadAcc() {
    const { companyType } = this.props;
    const accResp = await _request.accounts.getAll([], [], companyType);
    this.setState({ accounts: accResp?.data?.items || [] });
  }

  async loadData() {
    const accResp = await _request.accounts.getAll();
    const accounts = accResp?.data?.items || [];

    const resp = await _request.files.getAll();
    const files = resp?.data?.items || [];

    const draftResp = await _request.draftCollections.getAll();
    const draftCollections = draftResp?.data?.items || [];

    const postsResp = await _request.posts.getAll();
    const posts = postsResp?.data?.items || [];

    const groupResp = await _request.groups.getAll();
    const groups = groupResp?.data?.items || [];

    const res = await _request.users.getMeta();
    const metaData = res?.data?.user || {};

    this.setState({
      accounts,
      files,
      draftCollections,
      posts,
      groups,
      metaData: metaData || {},
    });
    this.postId();
  }

  componentDidMount() {
    super.componentDidMount();
    this.initializeDraftData();
  }

  initializeDraftData = () => {
    // Get post data from navigation state (from drafts or schedule page)
    const { editDraft, draftData, postToEdit } =
      this.props.location?.state || {};

    const dataToLoad = draftData || postToEdit;

    if (editDraft && dataToLoad) {
      const loadedData = loadDraftData(dataToLoad);

      if (loadedData) {
        // Ensure all arrays are actually arrays
        const safeLoadedData = {
          ...loadedData,
          tags: Array.isArray(loadedData.tags) ? loadedData.tags : [],
          selectedFiles: Array.isArray(loadedData.selectedFiles)
            ? loadedData.selectedFiles
            : [],
          selectedAccounts: Array.isArray(loadedData.selectedAccounts)
            ? loadedData.selectedAccounts
            : [],
        };

        // Inputs are controlled via `value={effectiveTitle/effectiveText}`
        // on the MUI TextFields, so writing state here flows straight into
        // the DOM on the next render.
        const nextState = {
          ...safeLoadedData,
          postID: dataToLoad.id,
        };
        if (loadedData.draftCollectionId) {
          nextState.selectedDrafts = [loadedData.draftCollectionId];
        }
        // Editing a scheduled post: keep it scheduled, and remember its tasks so they can be removed once the
        // replacement is created.
        const { replaceTaskIds, scheduleEnabled } =
          this.props.location?.state || {};
        if (Array.isArray(replaceTaskIds) && replaceTaskIds.length > 0) {
          nextState.replaceTaskIds = replaceTaskIds;
          nextState.scheduleEnabled = Boolean(scheduleEnabled);
        }
        this.setState(nextState);
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.lng !== this.props.lng) {
      moment.locale(this.props.lng);
    }
    if (prevProps.companyType !== this.props.companyType) {
      this.loadAcc();
    }
    // When the user deselects an account, drop any overrides for it.
    if (prevState && prevState.selectedAccounts !== this.state.selectedAccounts) {
      const prevSet = new Set(prevState.selectedAccounts || []);
      const currSet = new Set(this.state.selectedAccounts || []);
      const dropped = [...prevSet].some((id) => !currSet.has(id));
      if (dropped) this.pruneOverrides();
      this.loadTikTokCreatorInfoForSelected();
    }
    if (prevState && prevState.accounts !== this.state.accounts) {
      this.loadTikTokCreatorInfoForSelected();
    }

    // Check if navigation state changed and we have new draft data to load
    const prevNavState = prevProps.location?.state;
    const currentState = this.props.location?.state;

    if (
      currentState?.editDraft &&
      currentState?.draftData &&
      (!prevNavState?.editDraft ||
        prevNavState?.draftData?.id !== currentState?.draftData?.id) &&
      !this.state.postID // Only load if we haven't already loaded draft data
    ) {
      this.initializeDraftData();
    }
  }

  // Builds the shared (cross-group) part of a post payload — schedule, repeat,
  // dates. The per-group part (accounts, type_data, type) is added by
  // buildGroupPayload below.
  //
  // mode selects which schedule field the caller expects per docs/drafts-api.md:
  //   'publish' → schedule_utc_date (for /accounts/new-post)
  //   'draft'   → schedule_date     (for /accounts/save-draft and /posts/update/{id})
  getCommonPayload(mode = "publish") {
    const {
      selectedTime,
      selectedDate,
      toDate,
      repeatOption,
      metaData,
      scheduleEnabled,
    } = this.state;

    const { scheduleDate, scheduleUtcDate } = scheduleEnabled
      ? formatScheduleDate(selectedDate, selectedTime, metaData?.timezone)
      : { scheduleDate: null, scheduleUtcDate: null };

    const repeatEnd =
      toDate && selectedTime
        ? toDate.format("YYYY-MM-DD") + " " + selectedTime.format("HH:mm:ss")
        : null;

    const scheduleFields =
      mode === "draft"
        ? { schedule_date: scheduleDate }
        : { schedule_utc_date: scheduleUtcDate };

    return {
      ...scheduleFields,
      repeat_freq: repeatOption,
      repeat_end: repeatEnd,
    };
  }

  // Builds the full payload for one group (accounts sharing identical content).
  buildGroupPayload(group, common) {
    const { accounts, files: allFiles } = this.state;
    const { effective, accountIds } = group;
    const formattedTags = formatTagsForText(effective.tags);
    const typeData = buildTypeDataForGroup({
      effective,
      accountIds,
      accounts,
      formattedTags,
      files: allFiles,
    });
    const selectedFileIds = Array.isArray(effective.selectedFiles) ? effective.selectedFiles : [];
    const type = selectedFileIds.length > 0 ? CONTENT_TYPES.MEDIA : CONTENT_TYPES.TEXT;

    return {
      ...common,
      tags: effective.tags || [],
      type,
      accounts: accountIds,
      type_data: typeData,
    };
  }

  // Builds an array of payloads, one per content group. Returns at least one
  // group when accounts are selected; an empty array otherwise.
  buildAllGroupPayloads(mode = "publish") {
    const groups = buildPostGroups(this.state);
    if (groups.length === 0) return [];
    const common = this.getCommonPayload(mode);
    return groups.map((g) => this.buildGroupPayload(g, common));
  }

  // Single-row payload for updating an existing draft (one posts row ↔ one
  // set of accounts). Per-account overrides are collapsed to the base content.
  buildDraftUpdatePayload() {
    const { selectedAccounts, accounts, files: allFiles, selectedDrafts } = this.state;
    if (!Array.isArray(selectedAccounts) || selectedAccounts.length === 0) return null;

    const effective = getEffectiveData(this.state, null);
    const formattedTags = formatTagsForText(effective.tags);
    const typeData = buildTypeDataForGroup({
      effective,
      accountIds: selectedAccounts,
      accounts,
      formattedTags,
      files: allFiles,
    });
    const selectedFileIds = Array.isArray(effective.selectedFiles) ? effective.selectedFiles : [];
    const type = selectedFileIds.length > 0 ? CONTENT_TYPES.MEDIA : CONTENT_TYPES.TEXT;
    const common = this.getCommonPayload("draft");

    const payload = {
      ...common,
      tags: effective.tags || [],
      type,
      accounts: selectedAccounts,
      type_data: typeData,
    };

    const draftCollectionId =
      Array.isArray(selectedDrafts) && selectedDrafts.length > 0 ? selectedDrafts[0] : null;
    if (draftCollectionId) payload.draft_collection_id = draftCollectionId;

    return payload;
  }

  // Gating wrapper around the actual send. Order of precedence:
  // 1. errors  → hard-block with red alert
  // 2. pending → block with neutral "fill in required fields" alert
  // 3. warnings → open "Publish anyway?" confirmation dialog
  // 4. clean   → publish immediately
  postNow() {
    const map = this.getValidationMap();
    if (this.hasBlockingErrors(map)) {
      this.setState({
        alertErrorOpen: true,
        alertMessage: this.tr("postPage.validationBlocked"),
      });
      setTimeout(() => this.setState({ alertErrorOpen: false }), 3000);
      return;
    }
    if (this.hasPending(map)) {
      this.setState({
        alertErrorOpen: true,
        alertMessage: this.tr("postPage.completeRequired"),
      });
      setTimeout(() => this.setState({ alertErrorOpen: false }), 3000);
      return;
    }
    if (this.hasWarnings(map)) {
      this.setState({ confirmPublishOpen: true });
      return;
    }
    this._doPostNow();
  }

  // Actual fan-out to backend, bypassing the warning gate.
  // Called either directly from postNow (clean state) or from the
  // confirmation dialog (after user accepts warnings).
  //
  // When the user is editing a draft (postID + isDraftBeingEdited), publish
  // is a two-step operation per docs/drafts-api.md §6: delete the draft row,
  // then fan out new-post. We swallow a delete failure that indicates "gone
  // already" but abort the publish on any other error.
  _doPostNow() {
    const payloads = this.buildAllGroupPayloads("publish");
    if (payloads.length === 0) return;

    const { postID, isDraftBeingEdited } = this.state;
    const publishingDraft = Boolean(postID && isDraftBeingEdited);

    _request.accounts.loading(async () => {
      try {
        if (publishingDraft) {
          const del = await _request.posts.delete(postID);
          if (del && del.isOk === false) {
            this.setState({
              alertErrorOpen: true,
              alertMessage: this.tr("postPage.errorSendingPost"),
            });
            setTimeout(() => this.setState({ alertErrorOpen: false }), 3000);
            return;
          }
        }

        const results = await Promise.allSettled(
          payloads.map((p) => _request.accounts.newPost(p)),
        );
        const okCount = results.filter(
          (r) => r.status === "fulfilled" && r.value && r.value.isOk === true,
        ).length;
        const total = results.length;

        this.detectLinkedInReconnect(results, payloads);

        // Collect post IDs from successful creations so the status dialog
        // can poll /post-tasks/by-post/{id} for per-account progress.
        const postIds = results
          .filter((r) => r.status === "fulfilled" && r.value?.isOk === true)
          .map((r) => r.value?.data?.id)
          .filter((id) => id !== undefined && id !== null);

        if (okCount === total) {
          this.setState({
            alertOpen: true,
            alertMessage:
              total > 1
                ? `${total} ${this.tr("postPage.postCreatedSuccessfully")}`
                : this.tr("postPage.postCreatedSuccessfully"),
          });
          if (publishingDraft) {
            this.setState({ postID: null, isDraftBeingEdited: false });
          }
          await this.removeReplacedSchedule();
        } else {
          this.setState({
            alertErrorOpen: true,
            alertMessage:
              okCount > 0
                ? `${okCount}/${total} ${this.tr("postPage.postCreatedSuccessfully")}`
                : this.tr("postPage.errorSendingPost"),
          });
        }
        setTimeout(() => this.setState({ alertOpen: false }), 3000);

        // Only open the status dialog for immediate publishes. Scheduled
        // posts won't have status updates until the scheduled time.
        if (postIds.length > 0 && !this.state.scheduleEnabled) {
          this.setState({
            publishStatusOpen: true,
            publishStatusPostIds: postIds,
          });
        }
      } catch (error) {
        this.setState({
          alertErrorOpen: true,
          alertMessage: this.tr("postPage.errorOccurred"),
        });
      }
      setTimeout(() => this.setState({ alertErrorOpen: false }), 3000);
    }, this.onPostSend.bind(this));
  }

  // After a scheduled post was edited and its replacement was created, drop the tasks of the original. The new post
  // goes first so a failed create never loses the old one.
  async removeReplacedSchedule() {
    const { replaceTaskIds } = this.state;
    if (!Array.isArray(replaceTaskIds) || replaceTaskIds.length === 0) return;

    const results = await Promise.allSettled(
      replaceTaskIds.map((id) => _request.postTasks.delete(id)),
    );
    const failed = results.some(
      (r) => r.status === "rejected" || r.value?.isOk === false,
    );
    this.setState({ replaceTaskIds: [], postID: null });
    if (failed) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: this.tr("postPage.oldScheduleNotRemoved"),
      });
    }
  }

  // Scan per-group publish results for LI_TOKEN_INVALID and, when found,
  // raise a persistent "Reconnect LinkedIn" snackbar. account_type is
  // carried through so the accounts page can skip the profile/page picker.
  detectLinkedInReconnect(results, payloads) {
    const { accounts } = this.state;
    const accountsById = new Map((accounts || []).map((a) => [a.id, a]));
    const affectedTypes = new Set();

    results.forEach((r, idx) => {
      if (r.status !== "fulfilled") return;
      const resp = r.value;
      const errorCode = resp?.data?.error_code;
      if (errorCode !== "LI_TOKEN_INVALID") return;
      const accountIds = payloads[idx]?.accounts || [];
      accountIds.forEach((id) => {
        const acc = accountsById.get(id);
        if (acc?.social_type === "linkedin") {
          affectedTypes.add(acc.account_type || "profile");
        }
      });
    });

    if (affectedTypes.size === 0) return;
    const accountType = affectedTypes.size === 1 ? [...affectedTypes][0] : null;
    this.setState({ linkedinReconnectOpen: true, linkedinReconnectType: accountType });
  }

  // A dead token is normally discovered while the post is being published, not
  // in the create response detectLinkedInReconnect looks at — so the dialog
  // hands those failures over here and the same prompt is raised.
  handlePublishTokenFailures = (failures) => {
    const { accounts } = this.state;
    const accountsById = new Map((accounts || []).map((a) => [a.id, a]));
    const affectedTypes = new Set();

    (failures || []).forEach(({ accountId, socialType }) => {
      if (socialType !== "linkedin") return;
      const acc = accountsById.get(accountId);
      affectedTypes.add(acc?.account_type || "profile");
    });

    if (affectedTypes.size === 0) return;
    const accountType = affectedTypes.size === 1 ? [...affectedTypes][0] : null;
    this.setState({ linkedinReconnectOpen: true, linkedinReconnectType: accountType });
  };

  // "Reconnect" pressed on a specific failed row.
  handleReconnectFromTask = (task) => {
    // Threads has no special reconnect flow: the accounts page offers it on the account's card.
    if (task?.social_type === SOCIAL_TYPES.THREADS) {
      window.location.href = "/app/accounts";
      return;
    }
    if (task?.social_type !== "linkedin") return;
    const acc = (this.state.accounts || []).find((a) => a.id === task.account_id);
    this.setState(
      { linkedinReconnectType: acc?.account_type || "profile" },
      this.handleLinkedinReconnect,
    );
  };

  handleLinkedinReconnect = () => {
    const { linkedinReconnectType } = this.state;
    const qs = linkedinReconnectType
      ? `?reconnect_linkedin=${linkedinReconnectType}`
      : "?reconnect_linkedin=1";
    this.setState({ linkedinReconnectOpen: false });
    window.location.href = `/app/accounts${qs}`;
  };

  closeLinkedinReconnect = () => {
    this.setState({ linkedinReconnectOpen: false });
  };

  addDraftShow = (anchorEl) => {
    this.setState((prevState) => ({
      anchorEl: prevState.anchorEl ? null : anchorEl,
      popoverOpen: !prevState.popoverOpen,
    }));
  };

  handleDraftClick = (info) => {
    this.setState(
      (prevState) => {
        const isSelected = prevState.selectedDrafts.includes(info.id);
        const selectedDrafts = isSelected
          ? prevState.selectedDrafts.filter((draftId) => draftId !== info.id)
          : [...prevState.selectedDrafts, info.id];

        return {
          selectedDrafts,
          snackbarOpen: true,
          // The old string glued the collection name into a sentence about a
          // draft and appended a translated fragment, producing
          // "Draft <collection> has been Draft saved."
          snackbarMessage: isSelected
            ? this.tr("postPage.draftRemovedFrom", { collection: info.title })
            : this.tr("postPage.draftSavedTo", { collection: info.title }),
        };
      },
      () => {
        this.saveDraft();
      },
    );
  };

  // Creating the first collection used to be impossible from here — the
  // popover only listed what already existed, so an account with none could
  // not save a draft at all. Create it, then save into it in one go.
  handleCreateDraftCollection = async (title) => {
    const created = await _request.draftCollections.add({ title });

    if (!created?.isOk || !created?.data?.id) {
      this.setState({
        alertErrorOpen: true,
        alertMessage:
          created?.error || this.tr("postPage.failedToSaveDraft"),
      });
      setTimeout(() => this.setState({ alertErrorOpen: false }), 3000);
      return;
    }

    const collection = { id: created.data.id, title };
    this.setState((prev) => ({
      draftCollections: [...(prev.draftCollections || []), collection],
    }));
    this.handleDraftClick(collection);
  };

  handleSnackbarClose = () => {
    this.setState({ snackbarOpen: false });
  };

  popoverContent = () => {
    const { anchorEl, popoverOpen, draftCollections, selectedDrafts } =
      this.state;

    return (
      <DraftCollectionPopper
        anchorEl={anchorEl}
        popoverOpen={popoverOpen}
        draftCollections={draftCollections}
        onCreateCollection={this.handleCreateDraftCollection}
        selectedDrafts={selectedDrafts}
        onDraftClick={this.handleDraftClick}
        tr={this.tr.bind(this)}
      />
    );
  };

  saveDraft = async () => {
    const { postID, isDraftBeingEdited, selectedDrafts } = this.state;
    const updatingDraft = Boolean(postID && isDraftBeingEdited);

    // Update mode: one draft row, one payload. Overrides collapse to base
    // content — a single posts row can hold only one content variant.
    if (updatingDraft) {
      const payload = this.buildDraftUpdatePayload();
      if (!payload) return;

      try {
        await _request.posts.loading(async () => {
          return await _request.posts.update(postID, payload);
        });
      } catch (error) {
        console.error("Failed to update draft:", error);
        this.setState({
          snackbarOpen: true,
          snackbarMessage: this.tr("postPage.failedToSaveDraft"),
        });
      }
      return;
    }

    // Create mode: fan out save-draft per override-group.
    const payloads = this.buildAllGroupPayloads("draft");
    if (payloads.length === 0) return;

    const draftCollectionId =
      selectedDrafts && selectedDrafts.length > 0 ? selectedDrafts[0] : null;
    const finalPayloads = payloads.map((p) =>
      draftCollectionId ? { ...p, draft_collection_id: draftCollectionId } : p,
    );

    try {
      await _request.accounts.loading(async () => {
        return await Promise.allSettled(
          finalPayloads.map((p) => _request.accounts.saveDraft(p)),
        );
      }, this.onPostSend.bind(this));
    } catch (error) {
      console.error("Failed to save draft:", error);
      this.setState({
        snackbarOpen: true,
        snackbarMessage: this.tr("postPage.failedToSaveDraft"),
      });
    }
  };

  onPostSend(resp) {
  }

  getSelectAccsContent() {
    const { tempSelAccs, showAccountsInfo, open, accounts } = this.state;

    return (
      <AccountSelectionDialog
        open={open}
        onClose={this.handleClose}
        accounts={accounts}
        tempSelAccs={tempSelAccs}
        showAccountsInfo={showAccountsInfo}
        onToggleAccount={(id) => this.toggleStateArray("tempSelAccs", id)}
        onChangeSelectedAccounts={(accs) =>
          this.changeState("selectedAccounts", accs)
        }
        tr={this.tr.bind(this)}
      />
    );
  }

  onFilesSelectionChange(selectedFiles) {
    const next = Array.isArray(selectedFiles) ? selectedFiles : [];
    // The dialog was opened from a Threads chain part: the picked files are added to that part, after the ones
    // it already had (the dialog starts with nothing selected each time it opens).
    if (this.state.mediaTarget !== null && this.state.mediaTarget !== undefined) {
      const index = this.state.mediaTarget;
      const base = Array.isArray(this.state.mediaTargetBase) ? this.state.mediaTargetBase : [];
      const picked = next.filter((id) => typeof id === "number" || typeof id === "string");
      const files = [...base, ...picked.filter((id) => !base.includes(id))];
      this.setActiveFieldWith("threadsReplies", (prev) =>
        (Array.isArray(prev) ? prev : []).map((p, i) => (i === index ? { ...p, files } : p)),
      );
      return;
    }
    const typeContent = next.length > 0 ? CONTENT_TYPES.MEDIA : CONTENT_TYPES.TEXT;
    // typeContent is kept on base for backward compat with the legacy renderer;
    // for actual submission it's recomputed from effective files per group.
    this.setActiveField("selectedFiles", next, { typeContent });
  }

  handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    this.onFilesSelectionChange(files);
  };

  handleImageDragStart = (index, e) => {
    e.stopPropagation();
    // Use a plain string payload so the browser knows it's an internal drag
    e.dataTransfer.setData("text/x-image-reorder", String(index));
    e.dataTransfer.effectAllowed = "move";
    this.setState({ draggedImageIndex: index, isDragging: false });
  };

  handleImageDragEnd = () => {
    this.setState({ draggedImageIndex: null, dragOverIndex: null });
  };

  handleImageDragOverReorder = (toIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    const { draggedImageIndex } = this.state;
    if (draggedImageIndex === null || draggedImageIndex === toIndex) {
      this.setState({ dragOverIndex: toIndex });
      return;
    }
    const currentFiles = this.getActiveField("selectedFiles") || [];
    const newFiles = [...currentFiles];
    const [moved] = newFiles.splice(draggedImageIndex, 1);
    newFiles.splice(toIndex, 0, moved);
    this.onFilesSelectionChange(newFiles);
    this.setState({ draggedImageIndex: toIndex, dragOverIndex: toIndex });
  };

  handleDragEnter = (event) => {
    if (this.state.draggedImageIndex !== null) return;
    event.preventDefault();
    this.setState({ isDragging: true });
  };

  handleDragLeave = (event) => {
    event.preventDefault();
    this.setState({ isDragging: false });
  };

  // Re-fetch the canonical files list so freshly-uploaded files are present in
  // state.files. Validation (validateAccount → resolveFiles) resolves the
  // selected file ids against state.files; without this, a just-uploaded file's
  // id resolves to nothing and Instagram wrongly reports "requires at least one
  // file" even though the thumbnail (built from the id directly) is visible.
  refreshFiles = async () => {
    const resp = await _request.files.getAll();
    this.setState({ files: resp?.data?.items || [] });
  };

  handleDropUpload = async (event) => {
    // If we were reordering internal images — ignore this drop
    if (this.state.draggedImageIndex !== null) {
      this.setState({ draggedImageIndex: null, dragOverIndex: null, isDragging: false });
      return;
    }
    event.preventDefault();
    this.setState({ isDragging: false, isLoading: true });

    const files = Array.from(event.dataTransfer.files);

    const startTime = new Date();

    const response = await _request.files.loadingFile(async () => {
      return await _request.files.upload(null, files);
    });

    const endTime = new Date();
    const uploadTimeInMillis = endTime - startTime;
    const uploadTimeInSeconds = uploadTimeInMillis / 100;

    this.setState((prevState) => ({
      totalUploadTime: prevState.totalUploadTime + uploadTimeInSeconds,
      isLoading: false,
    }));

    if (response.isOk === true && response.data && response.data.length > 0) {
      const uploadedIds = response.data.map((item) => item.original.file.id);
      // Pull the new files into state.files before selecting them, so per-account
      // validation can resolve their ids (see refreshFiles).
      await this.refreshFiles();
      this.setState({ selectedFiles: uploadedIds });
      this.onFilesSelectionChange(uploadedIds);
    } else {
      this.setState({ isLoading: false });
      console.error("Upload failed", response);
    }
  };

  getSelectImage(files, accept) {
    return (
      <MediaUploadDialog
        open={this.state.openIconsDialog}
        onClose={this.handleCloseImageDialog}
        files={files}
        accept={accept}
        onDrop={this.handleDrop}
        onDragEnter={this.handleDragEnter}
        onDragLeave={this.handleDragLeave}
        onSelectionChange={this.onFilesSelectionChange.bind(this)}
        onNewFolder={this.newFolder.bind(this)}
        onUploadDialog={this.uploadDialog.bind(this)}
        refresh={this.refreshFiles}
        filesManagerRef={(el) => (this.filesManager = el)}
        tr={this.tr.bind(this)}
      />
    );
  }

  newFolder() {
    this.filesManager?.newFolder();
  }

  uploadDialog() {
    this.filesManager?.uploadDialog();
  }
  handleClickOpen() {
    // Синхронизируем tempSelAccs с selectedAccounts при открытии диалога
    this.setState({
      open: true,
      tempSelAccs: [...this.state.selectedAccounts],
    });
  }

  handleClose() {
    this.setState({ open: false });
  }

  handleCloseImageDialog() {
    this.setState({ openIconsDialog: false, mediaTarget: null, mediaTargetBase: [] });
  }

  // "Add media" on a Threads chain part: the shared media dialog fills that part instead of part 1.
  openThreadsPartMedia = (index) => {
    const parts = this.getActiveField("threadsReplies") || [];
    const part = parts[index];
    if (!part) return;
    this.setState({
      openIconsDialog: true,
      mediaTarget: index,
      mediaTargetBase: Array.isArray(part.files) ? [...part.files] : [],
    });
  };

  removeThreadsPartMedia = (index, fileId) => {
    this.setActiveFieldWith("threadsReplies", (prev) =>
      (Array.isArray(prev) ? prev : []).map((p, i) =>
        i === index ? { ...p, files: (p.files || []).filter((id) => id !== fileId) } : p,
      ),
    );
  };

  handleThreadsRepliesChange = (next) => {
    this.setActiveField("threadsReplies", Array.isArray(next) ? next : [], { isEditing: true });
  };

  handleTelegramSettingsChange = ({ pin_message, pin_silent, inline_keyboard }) => {
    // Telegram settings are written as a small batch, but each field still
    // routes through setActiveField so it lands in the right scope.
    this.setActiveField("telegramPinMessage", pin_message);
    this.setActiveField("telegramPinSilent", pin_silent);
    this.setActiveField("telegramInlineKeyboard", inline_keyboard);
  };

  handleInstagramSettingsChange = ({ post_type }) => {
    this.setActiveField("instagramPostType", post_type);
  };

  // ─── TikTok Creator Info ────────────────────────────────────────────────
  // The TikTok settings UI needs per-account creator info (privacy options,
  // forced disable toggles, max video duration). We lazy-fetch it for every
  // selected TikTok account and cache the result in state. Backend caches for
  // 1h; pass refresh=true to bust.
  loadTikTokCreatorInfoForSelected = () => {
    const { accounts, selectedAccounts, tiktokCreatorInfoByAccount } = this.state;
    if (!Array.isArray(accounts) || !Array.isArray(selectedAccounts)) return;
    const cache = tiktokCreatorInfoByAccount || {};
    const toFetch = accounts.filter(
      (a) =>
        a &&
        selectedAccounts.includes(a.id) &&
        a.social_type === SOCIAL_TYPES.TIKTOK &&
        !cache[a.id],
    );
    toFetch.forEach((a) => this.fetchTikTokCreatorInfo(a.id, false));
  };

  fetchTikTokCreatorInfo = async (accountId, refresh = false) => {
    this.setState((prev) => ({
      tiktokCreatorInfoByAccount: {
        ...(prev.tiktokCreatorInfoByAccount || {}),
        [accountId]: { data: null, loading: true, error: null },
      },
    }));
    try {
      const resp = await _request.accounts.getTikTokCreatorInfo(accountId, refresh);
      if (resp && resp.isOk) {
        this.setState((prev) => ({
          tiktokCreatorInfoByAccount: {
            ...(prev.tiktokCreatorInfoByAccount || {}),
            [accountId]: { data: resp.data, loading: false, error: null },
          },
        }));
      } else {
        this.setState((prev) => ({
          tiktokCreatorInfoByAccount: {
            ...(prev.tiktokCreatorInfoByAccount || {}),
            [accountId]: {
              data: null,
              loading: false,
              error: resp?.error || "Unknown error",
            },
          },
        }));
      }
    } catch (e) {
      this.setState((prev) => ({
        tiktokCreatorInfoByAccount: {
          ...(prev.tiktokCreatorInfoByAccount || {}),
          [accountId]: { data: null, loading: false, error: e.message || "Request failed" },
        },
      }));
    }
  };

  // Returns the account id whose Creator Info should drive the TikTok settings
  // panel. In override mode: the active account. In base mode: the first
  // selected TikTok account (users with multiple TikTok accounts that need
  // different settings should use per-account overrides).
  getActiveTikTokAccountId() {
    const { accounts, selectedAccounts, activeOverrideAccountId } = this.state;
    const safeAccounts = Array.isArray(accounts) ? accounts : [];
    if (activeOverrideAccountId) {
      const acc = safeAccounts.find((a) => a && a.id === activeOverrideAccountId);
      if (acc && acc.social_type === SOCIAL_TYPES.TIKTOK) return acc.id;
      return null;
    }
    const first = safeAccounts.find(
      (a) =>
        a &&
        a.social_type === SOCIAL_TYPES.TIKTOK &&
        (selectedAccounts || []).includes(a.id),
    );
    return first ? first.id : null;
  }

  // ─── Per-account override helpers ──────────────────────────────────────────

  // Writes a field either to the active account override (if a tab is active)
  // or to the base state (default behavior).
  setActiveField = (field, value, extraState = null) => {
    const activeId = this.state.activeOverrideAccountId;
    if (!activeId) {
      this.setState({ [field]: value, ...(extraState || {}) });
      return;
    }
    this.setState((prev) => {
      const prevOv = prev.accountOverrides || {};
      const nextOv = {
        ...prevOv,
        [activeId]: { ...(prevOv[activeId] || {}), [field]: value },
      };
      return { accountOverrides: nextOv, ...(extraState || {}) };
    });
  };

  // Same as setActiveField but takes an updater (prev value → next value).
  setActiveFieldWith = (field, updater) => {
    const activeId = this.state.activeOverrideAccountId;
    this.setState((prev) => {
      const current = getEffectiveField(prev, field, activeId);
      const next = updater(current);
      if (!activeId) return { [field]: next };
      const prevOv = prev.accountOverrides || {};
      return {
        accountOverrides: {
          ...prevOv,
          [activeId]: { ...(prevOv[activeId] || {}), [field]: next },
        },
      };
    });
  };

  // Reads the effective value of a field for the currently active scope.
  getActiveField = (field) =>
    getEffectiveField(this.state, field, this.state.activeOverrideAccountId);

  // Switches the active edit scope. null = base (all accounts).
  // Also syncs the preview tab so the user immediately sees what they're editing.
  setActiveOverride = (accountId) => {
    const update = { activeOverrideAccountId: accountId };
    if (accountId) update.selectedPreviewAccountId = accountId;
    this.setState(update);
  };

  // Removes all overrides for an account.
  clearAccountOverride = (accountId) => {
    this.setState((prev) => {
      const next = { ...(prev.accountOverrides || {}) };
      delete next[accountId];
      const stateUpdate = { accountOverrides: next };
      if (prev.activeOverrideAccountId === accountId) {
        stateUpdate.activeOverrideAccountId = null;
      }
      return stateUpdate;
    });
  };

  // Cleans up overrides for accounts that are no longer selected.
  // Should be called whenever selectedAccounts changes (deselect).
  pruneOverrides = () => {
    this.setState((prev) => {
      const selected = new Set(prev.selectedAccounts || []);
      const cleaned = {};
      let changed = false;
      for (const [id, ov] of Object.entries(prev.accountOverrides || {})) {
        if (selected.has(id)) cleaned[id] = ov;
        else changed = true;
      }
      const update = changed ? { accountOverrides: cleaned } : {};
      if (prev.activeOverrideAccountId && !selected.has(prev.activeOverrideAccountId)) {
        update.activeOverrideAccountId = null;
      }
      return update;
    });
  };

  handleAddTag = (newTag) => {
    this.setActiveFieldWith("tags", (prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return [...arr, newTag];
    });
  };

  handleRemoveTag = (indexToRemove) => {
    this.setActiveFieldWith("tags", (prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.filter((_, i) => i !== indexToRemove);
    });
  };

  handleTimeChange = (time) => {
    if (time && time.isValid()) {
      this.setState({
        selectedTime: time,
      });
    } else {
      console.error("Invalid time format or null time received");
    }
  };

  handleDateChange = (date) => {
    const { activeDate, repeatOption } = this.state;
    if (activeDate === ACTIVE_DATE_TYPES.FROM) {
      const minToDate = getMinToDate(date, repeatOption);

      this.setState({
        fromDate: date,
        selectedDate: date, // sync selectedDate for scheduling
        minToDate: minToDate,
      });
    } else if (activeDate === ACTIVE_DATE_TYPES.TO) {
      this.setState({ toDate: date });
    }
  };

  handleChange = (event) => {
    const repeatOption = event.target.value;

    this.setState({ repeatOption }, () => {
      if (this.state.fromDate) {
        const minToDate = getMinToDate(this.state.fromDate, repeatOption);
        this.setState({ minToDate });
      }
    });
  };

  toggleFilesSelection(id) {
    this.filesManager?.toggleSelection(id);
  }

  // ─── NEW REDESIGNED RENDER METHODS ────────────────────────────────────────

  renderComposeCard(isTrialEndedParam) {
    const { accounts, selectedAccounts, totalUploadTime, files, metaData, activeOverrideAccountId, accountOverrides } = this.state;
    const safeAccounts = Array.isArray(accounts) ? accounts : [];
    const safeSelectedAccounts = Array.isArray(selectedAccounts) ? selectedAccounts : [];
    const accs = safeAccounts.filter((it) => it && safeSelectedAccounts.includes(it.id));
    const { files_extensions } = metaData || {};
    const accept = _helper.formFilesAccept(files_extensions || []);

    // Read fields through the active scope (base or override).
    const effectiveText = this.getActiveField("postText") || "";
    const effectiveTitle = this.getActiveField("postTitle") || "";
    const effectiveFiles = this.getActiveField("selectedFiles") || [];
    const safeSelectedFiles = Array.isArray(effectiveFiles) ? effectiveFiles : [];
    const showOverrideTabs = safeSelectedAccounts.length > 1;
    // Dynamic char limit: in override mode it's the active account's limit;
    // in base mode it's the min across all selected accounts (so the user
    // sees the tightest constraint they need to fit). Falls back to 2200
    // when no platform has an opinion.
    // Each network counts in its own way (Threads adds the hashtags and counts emojis by their UTF-8 bytes), so
    // the counter shows the account with the least room left.
    const computeCounter = () => {
      const ids = activeOverrideAccountId
        ? [activeOverrideAccountId]
        : safeSelectedAccounts;
      const counters = ids
        .map((id) => safeAccounts.find((a) => a && a.id === id))
        .filter(Boolean)
        .map((acc) => getTextCounterForAccount(acc, getEffectiveData(this.state, acc.id)))
        .filter((c) => Number.isFinite(c.limit));
      if (counters.length === 0) return { used: effectiveText.length, limit: 2200 };
      return counters.reduce((tightest, c) =>
        c.limit - c.used < tightest.limit - tightest.used ? c : tightest,
      );
    };
    const { used: textLen, limit: MAX_CHARS } = computeCounter();
    const validationMap = this.getValidationMap();
    // Issues for the currently active scope (specific account, or aggregated
    // across all accounts when in base mode).
    const activeScopeIssues = activeOverrideAccountId
      ? validationMap[activeOverrideAccountId] || []
      : safeSelectedAccounts.flatMap((id) =>
          (validationMap[id] || []).map((iss) => ({ ...iss, accountId: id })),
        );

    return (
      <Box className="post-card">
        {/* Header */}
        <Box className="post-card-header">
          <Typography className="post-card-title">
            {this.tr("postPage.compose")}
          </Typography>
          <Box
            className="btnDark post-ai-btn"
            onClick={() => this.setState({ aiDialogOpen: true })}
            sx={{ cursor: isTrialEndedParam ? "not-allowed" : "pointer", opacity: isTrialEndedParam ? 0.5 : 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            {this.tr("aiGenerate.button")}
          </Box>
        </Box>

        {/* Account selector row */}
        <Box className="post-accounts-row">
          <Box className="post-accounts-list">
            {accs.length === 0 && (
              <Typography className="post-no-accounts-text">
                {this.tr("postPage.noAccountSelected")}
              </Typography>
            )}
            {accs.map((info) => {
              const { id, username, social_type, avatar } = info;
              return (
                <Box
                  key={id}
                  className="post-account-chip"
                  title={username}
                  sx={{ "&:hover .close-chip": { display: "flex !important" } }}>
                  <AvatarIcon avatar={avatar} width="36px" height="36px" />
                  <Box sx={{ position: "absolute", bottom: -3, right: -3 }}>
                    <IconsAccounts width={16} height={16} socialType={social_type} />
                  </Box>
                  <IconButton
                    className="close-chip"
                    sx={{
                      position: "absolute", top: -4, right: -4,
                      display: "none", width: 16, height: 16, padding: 0,
                      backgroundColor: "rgba(0,0,0,0.65)",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.9)" },
                    }}
                    onClick={() => this.toggleStateArray("selectedAccounts", id)}>
                    <CloseIcon sx={{ fontSize: 10, color: "#fff" }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
          <Box
            className="btnDark"
            onClick={isTrialEndedParam ? null : this.handleClickOpen}
            sx={{ cursor: isTrialEndedParam ? "not-allowed" : "pointer", opacity: isTrialEndedParam ? 0.5 : 1, flexShrink: 0, fontSize: "13px" }}>
            {this.tr("postPage.chooseAccount")}
          </Box>
          {this.getSelectAccsContent()}
        </Box>

        <Box className="post-card-divider" />

        {/* Per-account override tab strip (only when 2+ accounts selected) */}
        {showOverrideTabs && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              padding: "10px 20px 4px",
              alignItems: "center",
            }}>
            <Box
              onClick={() => this.setActiveOverride(null)}
              sx={{
                cursor: "pointer",
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "12px",
                fontWeight: 600,
                backgroundColor: !activeOverrideAccountId ? "#42C0C7" : "transparent",
                color: !activeOverrideAccountId ? "#fff" : "var(--text-color)",
                border: !activeOverrideAccountId ? "1px solid #42C0C7" : "1px solid var(--border-color, #ffffff33)",
                opacity: !activeOverrideAccountId ? 1 : 0.7,
                transition: "all 0.15s",
                "&:hover": { opacity: 1, borderColor: "#42C0C7" },
              }}>
              {this.tr("postPage.allAccounts") || "All accounts"}
            </Box>
            {accs.map((info) => {
              const { id, username, social_type } = info;
              const isActive = activeOverrideAccountId === id;
              const overridden = hasOverride(accountOverrides, id);
              const status = issuesStatus(validationMap[id] || []);
              // Pending issues are not surfaced on the tab — they're shown
              // in the neutral "Required to publish" panel below.
              const visibleStatus = status === "pending" ? "ok" : status;
              const statusColor =
                visibleStatus === "error" ? "#e53935" : visibleStatus === "warn" ? "#f5a623" : null;
              const statusBorder = isActive
                ? "#42C0C7"
                : statusColor || "var(--border-color, #ffffff33)";
              return (
                <Box
                  key={id}
                  onClick={() => this.setActiveOverride(id)}
                  title={username}
                  sx={{
                    cursor: "pointer",
                    padding: "4px 10px 4px 6px",
                    borderRadius: "16px",
                    fontSize: "12px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    backgroundColor: isActive ? "#42C0C7" : "transparent",
                    color: isActive ? "#fff" : "var(--text-color)",
                    border: `1px solid ${statusBorder}`,
                    opacity: isActive ? 1 : 0.85,
                    transition: "all 0.15s",
                    "&:hover": { opacity: 1, borderColor: isActive ? "#42C0C7" : statusColor || "#42C0C7" },
                  }}>
                  <IconsAccounts width={16} height={16} socialType={social_type} />
                  <span style={{ maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {username || social_type}
                  </span>
                  {visibleStatus !== "ok" && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        fontSize: "10px",
                        fontWeight: 700,
                        backgroundColor: isActive ? "rgba(255,255,255,0.25)" : statusColor,
                        color: "#fff",
                      }}>
                      {visibleStatus === "error" ? "!" : "?"}
                    </Box>
                  )}
                  {overridden && (
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.clearAccountOverride(id);
                      }}
                      title={this.tr("postPage.resetOverride") || "Reset to base"}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: isActive ? "rgba(255,255,255,0.25)" : "rgba(66,192,199,0.2)",
                        fontSize: "10px",
                      }}>
                      ×
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Validation issues panel for the active scope.
            Split into three sections so "missing content" hints don't look
            like errors when the user has just opened the page. */}
        {this.renderIssuesPanel(activeScopeIssues, accs)}

        {/* Title field */}
        {this.insertGlobalInput({
          value: effectiveTitle,
          onChange: this.handleTitleChange,
          InputProps: {
            className: "font f16",
            placeholder: this.tr("postPage.titlePlaceholder"),
            sx: { backgroundColor: "transparent", color: "var(--input-text)", "& fieldset": { border: "none" }, borderRadius: 0 },
          },
          size: "large",
          multiline: true,
          rows: 1,
        })}

        {/* Text / caption field with char counter */}
        <Box sx={{ position: "relative" }}>
          {this.insertGlobalInput({
            value: effectiveText,
            InputProps: {
              className: "font f14",
              placeholder: this.tr("postPage.captionPlaceholder"),
              sx: { backgroundColor: "transparent", color: "var(--input-text)", "& fieldset": { border: "none" }, borderRadius: 0 },
            },
            size: "small",
            multiline: true,
            rows: 6,
            onChange: this.handleTextChange,
          })}
          <Typography
            sx={{
              position: "absolute", bottom: 8, right: 20,
              fontSize: "11px", opacity: 0.5,
              color: textLen > MAX_CHARS ? "error.main" : "var(--text-color)",
            }}>
            {textLen} / {MAX_CHARS}
          </Typography>
        </Box>

        <Box className="post-card-divider" />

        {/* Inline media row */}
        <Box
          className="post-media-row"
          onDrop={this.handleDropUpload}
          onDragOver={this.handleDragEnter}
          onDragLeave={this.handleDragLeave}
          onDragEnter={this.handleDragEnter}>
          {this.state.isLoading && (
            <Box className="post-media-thumb post-media-loading">
              <BrokenImageOutlinedIcon sx={{ fontSize: "24px", color: "#A0A0A0" }} />
              <LinearDeterminate uploadTime={totalUploadTime} sx={{ position: "absolute", inset: 0, zIndex: 1 }} />
            </Box>
          )}
          {safeSelectedFiles.map((id, i) => (
            <Box
              key={i}
              className="post-media-thumb"
              draggable
              onDragStart={(e) => this.handleImageDragStart(i, e)}
              onDragEnd={this.handleImageDragEnd}
              onDragOver={(e) => this.handleImageDragOverReorder(i, e)}
              sx={{
                "&:hover .close-chip": { display: "flex !important" },
                cursor: "grab",
                opacity: this.state.draggedImageIndex === i ? 0.35 : 1,
                outline: this.state.dragOverIndex === i && this.state.draggedImageIndex !== null && this.state.draggedImageIndex !== i
                  ? "2px dashed #49B1B5" : "none",
                borderRadius: "8px",
                transition: "opacity 0.15s",
              }}>
              <ImageError
                src={_request.files.formLink(id)}
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px" }}
                onClick={() => this.toggleFilesSelection(id)}
              />
              <IconButton
                className="close-chip"
                sx={{
                  position: "absolute", top: 2, right: 2,
                  display: "none", width: 18, height: 18, padding: 0,
                  backgroundColor: "rgba(0,0,0,0.65)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.9)" },
                }}
                onClick={() => this.handleFileRemove(id)}>
                <Close sx={{ fontSize: 12, color: "#fff" }} />
              </IconButton>
            </Box>
          ))}
          <Box
            onClick={() => this.setState({ openIconsDialog: true, mediaTarget: null, mediaTargetBase: [] })}
            className={`post-media-add ${this.state.isDragging ? "dragging" : ""}`}>
            <AddIcon sx={{ color: "#49B1B5", fontSize: "28px" }} />
          </Box>
          {safeSelectedFiles.length === 0 && !this.state.isLoading && (
            <Box className={`post-media-dropzone ${this.state.isDragging ? "dragging" : ""}`}>
              <Typography sx={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.5, textAlign: "center", lineHeight: 1.6 }}>
                {this.tr("postPage.dragDropFiles")}<br />{this.tr("postPage.orClickUpload")}
              </Typography>
            </Box>
          )}
          {this.getSelectImage(files, accept)}
        </Box>
      </Box>
    );
  }

  renderTagsCard() {
    const tags = this.getActiveField("tags");
    const safeTags = Array.isArray(tags) ? tags : [];
    const effectiveText = this.getActiveField("postText") || "";
    return (
      <Box className="post-card post-card-sm">
        <Box className="post-card-header post-card-header-sm">
          <Typography className="post-card-title">
            {this.tr("postPage.tags")}
          </Typography>
        </Box>
        <Box sx={{ padding: "0 20px 16px" }}>
          <TagInput
            tags={safeTags}
            onAddTag={this.handleAddTag}
            text={effectiveText}
            onRemoveTag={this.handleRemoveTag}
          />
        </Box>
      </Box>
    );
  }

  // ─── Validation helpers ────────────────────────────────────────────────────

  // Returns issues for a single account based on its current effective content.
  getIssuesForAccount(accountId) {
    const { accounts, files } = this.state;
    const acc = (accounts || []).find((a) => a && a.id === accountId);
    if (!acc) return [];
    const effective = getEffectiveData(this.state, accountId);
    return validateAccount(acc, effective, files);
  }

  // Returns a map { accountId: issues[] } for every selected account.
  getValidationMap() {
    const { selectedAccounts } = this.state;
    const map = {};
    for (const id of selectedAccounts || []) {
      map[id] = this.getIssuesForAccount(id);
    }
    return map;
  }

  // True if any selected account has at least one error-level issue.
  hasBlockingErrors(validationMap) {
    const map = validationMap || this.getValidationMap();
    return Object.values(map).some((issues) =>
      issues.some((i) => i.severity === SEVERITY.ERROR),
    );
  }

  // True if any selected account has at least one warn-level issue.
  hasWarnings(validationMap) {
    const map = validationMap || this.getValidationMap();
    return Object.values(map).some((issues) =>
      issues.some((i) => i.severity === SEVERITY.WARN),
    );
  }

  // Counts warn-level issues across all accounts.
  countWarnings(validationMap) {
    const map = validationMap || this.getValidationMap();
    return Object.values(map).reduce(
      (acc, issues) => acc + issues.filter((i) => i.severity === SEVERITY.WARN).length,
      0,
    );
  }

  // True if any account has at least one pending (missing-content) issue.
  // Pending blocks Publish but is shown as a neutral hint, not as an error.
  hasPending(validationMap) {
    const map = validationMap || this.getValidationMap();
    return Object.values(map).some((issues) =>
      issues.some((i) => i.severity === SEVERITY.PENDING),
    );
  }

  // Returns true if the settings panel for `socialType` should be shown.
  // In base mode: any selected account of that type.
  // In override mode: only if the active account is of that type.
  shouldShowSettingsFor(socialType) {
    const { accounts, selectedAccounts, activeOverrideAccountId } = this.state;
    const safeAccounts = Array.isArray(accounts) ? accounts : [];
    const safeSelected = Array.isArray(selectedAccounts) ? selectedAccounts : [];
    if (activeOverrideAccountId) {
      const acc = safeAccounts.find((a) => a && a.id === activeOverrideAccountId);
      return Boolean(acc && acc.social_type === socialType);
    }
    return safeAccounts.some(
      (acc) => acc && safeSelected.includes(acc.id) && acc.social_type === socialType,
    );
  }

  renderYouTubeSettingsCard() {
    if (!this.shouldShowSettingsFor(SOCIAL_TYPES.YOUTUBE)) return null;
    const privacy = this.getActiveField("youtubePrivacy");
    const categoryId = this.getActiveField("youtubeCategoryId");
    const title = this.getActiveField("postTitle") || "";
    const text = this.getActiveField("postText") || "";
    return (
      <YouTubeSettings
        privacy={privacy}
        categoryId={categoryId}
        onPrivacyChange={(val) => this.setActiveField("youtubePrivacy", val)}
        onCategoryChange={(val) => this.setActiveField("youtubeCategoryId", val)}
        titleLength={title.length}
        textLength={text.length}
      />
    );
  }

  renderTelegramSettingsCard() {
    if (!this.shouldShowSettingsFor(SOCIAL_TYPES.TELEGRAM)) return null;
    return (
      <TelegramSettings
        pinMessage={this.getActiveField("telegramPinMessage")}
        pinSilent={this.getActiveField("telegramPinSilent")}
        inlineKeyboard={this.getActiveField("telegramInlineKeyboard")}
        onChange={this.handleTelegramSettingsChange}
      />
    );
  }

  renderInstagramSettingsCard() {
    if (!this.shouldShowSettingsFor(SOCIAL_TYPES.INSTAGRAM)) return null;
    return (
      <InstagramSettings
        postType={this.getActiveField("instagramPostType")}
        onChange={this.handleInstagramSettingsChange}
      />
    );
  }

  renderTikTokSettingsCard() {
    if (!this.shouldShowSettingsFor(SOCIAL_TYPES.TIKTOK)) return null;
    const accountId = this.getActiveTikTokAccountId();
    if (!accountId) return null;

    const cache = this.state.tiktokCreatorInfoByAccount || {};
    const entry = cache[accountId] || { data: null, loading: false, error: null };

    const fileIds = this.getActiveField("selectedFiles") || [];
    const files = this.state.files || [];
    const filesById = new Map(files.map((f) => [f.id, f]));
    const isPhotoCarousel =
      fileIds.length > 0 &&
      fileIds.every((id) => {
        const f = filesById.get(id);
        if (!f) return false;
        return f.file_type === "image" || (f.mime_type && f.mime_type.startsWith("image/"));
      });

    const values = {
      tiktokPrivacyLevel: this.getActiveField("tiktokPrivacyLevel"),
      tiktokPublishMode: this.getActiveField("tiktokPublishMode"),
      tiktokDisableComment: this.getActiveField("tiktokDisableComment"),
      tiktokDisableDuet: this.getActiveField("tiktokDisableDuet"),
      tiktokDisableStitch: this.getActiveField("tiktokDisableStitch"),
      tiktokVideoCoverTimestampMs: this.getActiveField("tiktokVideoCoverTimestampMs"),
      tiktokPhotoCoverIndex: this.getActiveField("tiktokPhotoCoverIndex"),
      tiktokAutoAddMusic: this.getActiveField("tiktokAutoAddMusic"),
      tiktokBrandContentToggle: this.getActiveField("tiktokBrandContentToggle"),
      tiktokBrandOrganicToggle: this.getActiveField("tiktokBrandOrganicToggle"),
    };

    return (
      <TikTokSettings
        creatorInfo={entry.data}
        creatorInfoLoading={entry.loading}
        creatorInfoError={entry.error}
        onRefreshCreatorInfo={() => this.fetchTikTokCreatorInfo(accountId, true)}
        isPhotoCarousel={isPhotoCarousel}
        fileCount={fileIds.length}
        values={values}
        onChange={(field, value) => this.setActiveField(field, value)}
      />
    );
  }

  // Threads accounts the chain editor is about: the active tab's account, or every selected Threads account.
  getThreadsScopeAccounts() {
    const { accounts, selectedAccounts, activeOverrideAccountId } = this.state;
    const safeAccounts = Array.isArray(accounts) ? accounts : [];
    const ids = activeOverrideAccountId ? [activeOverrideAccountId] : selectedAccounts || [];
    return safeAccounts.filter(
      (a) => a && ids.includes(a.id) && a.social_type === SOCIAL_TYPES.THREADS,
    );
  }

  renderThreadsThreadCard() {
    if (!this.shouldShowSettingsFor(SOCIAL_TYPES.THREADS)) return null;
    const parts = this.getActiveField("threadsReplies") || [];
    const threadsAccounts = this.getThreadsScopeAccounts();
    // Before App Review the app does not even ask for the permission: no chains, so no card. A chain that is
    // already there (a draft) stays visible, so it can be removed.
    const allUnavailable =
      threadsAccounts.length > 0 && threadsAccounts.every((a) => a.threads_replies === "unavailable");
    if (allUnavailable && parts.length === 0) return null;
    const reconnectRequired = threadsAccounts.some((a) => a.threads_replies === "reconnect_required");

    const { accounts, selectedAccounts, activeOverrideAccountId } = this.state;
    const showOtherNetworksNote =
      !activeOverrideAccountId &&
      (accounts || []).some(
        (a) => a && (selectedAccounts || []).includes(a.id) && a.social_type !== SOCIAL_TYPES.THREADS,
      );

    // Messages about a single reply, shown under it (the issues panel above lists them too).
    const validationMap = this.getValidationMap();
    const issuesByPart = {};
    threadsAccounts.forEach((acc) => {
      (validationMap[acc.id] || []).forEach((iss) => {
        const part = iss.params && iss.params.part;
        if (!part || part < 2 || iss.severity !== SEVERITY.ERROR) return;
        const index = part - 2;
        const message = formatIssue((k) => this.tr(k), iss);
        issuesByPart[index] = issuesByPart[index] || [];
        if (!issuesByPart[index].includes(message)) issuesByPart[index].push(message);
      });
    });

    return (
      <ThreadsThreadEditor
        parts={parts}
        issuesByPart={issuesByPart}
        onChange={this.handleThreadsRepliesChange}
        onPickMedia={this.openThreadsPartMedia}
        onRemoveMedia={this.removeThreadsPartMedia}
        showOtherNetworksNote={showOtherNetworksNote}
        reconnectRequired={reconnectRequired}
        tr={this.tr.bind(this)}
      />
    );
  }

  renderScheduleCard() {
    const { scheduleEnabled } = this.state;
    return (
      <Box className="post-card post-card-sm">
        <Box className="post-card-header post-card-header-sm" sx={{ justifyContent: "space-between" }}>
          <Typography className="post-card-title">
            {this.tr("postPage.schedule")}
          </Typography>
          <IOSSwitch
            checked={Boolean(scheduleEnabled)}
            onChange={(e) => this.setState({ scheduleEnabled: e.target.checked })}
          />
        </Box>
        {scheduleEnabled ? (
          <DateScheduler
            repeatOption={this.state.repeatOption}
            activeDate={this.state.activeDate}
            fromDate={this.state.fromDate}
            toDate={this.state.toDate}
            selectedTime={this.state.selectedTime}
            minToDate={this.state.minToDate}
            onRepeatChange={this.handleChange}
            onActiveDateChange={(activeDate) => this.setState({ activeDate })}
            onDateChange={this.handleDateChange}
            onTimeChange={this.handleTimeChange}
            language={i18n.language}
            tr={this.tr.bind(this)}
          />
        ) : (
          <Box sx={{ padding: "8px 20px 16px" }}>
            <Typography sx={{ fontSize: "13px", color: "var(--text-color)", opacity: 0.55 }}>
              {this.tr("postPage.publishImmediately")}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // ─── LEGACY renderPostBody (kept for backward compat — replaced in renderPage) ──
  renderPostBody_LEGACY(isTrialEndedParam) {
    const { accounts, selectedAccounts, tags, selectedFiles, totalUploadTime } =
      this.state;

    // Active-scope values drive the controlled Title/Topic TextFields so AI
    // apply, draft load, and per-account override tab switches all flow into
    // the inputs instead of getting stuck on whatever the user last typed.
    const effectiveTitle = this.getActiveField("postTitle") || "";
    const effectiveText = this.getActiveField("postText") || "";

    // Ensure tags is always an array
    const safeTags = Array.isArray(tags) ? tags : [];
    const safeAccounts = Array.isArray(accounts) ? accounts : [];
    const safeSelectedAccounts = Array.isArray(selectedAccounts)
      ? selectedAccounts
      : [];

    const accs = safeAccounts.filter(
      (it) => it && safeSelectedAccounts.includes(it.id),
    );
    const { metaData } = this.state;
    const { files } = this.state;

    const { files_extensions } = metaData || {};
    const accept = _helper.formFilesAccept(files_extensions || []);

    const postsBox = (
      <Box className="flex1 flex column gap16">
        <Box sx={{ padding: "20px", borderBottom: "1px solid #FFFFFF3D" }}>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: "bold",
              color: "var(--text-color)",
            }}>
            {this.tr("postPage.postTo")}
          </Typography>
        </Box>
        <Box className="flex column gap4">
          <Box
            className="content-center"
            sx={{
              backgroundColor: "var(--background-preview)",
              height: "64px",
              margin: { xs: "0 5px", md: "0 40px" },
              borderRadius: "7px",
            }}>
            <Box className="flex between mx-5 items-center">
              <Box>
                <List component="div" className="flex1 flex wrap gap16">
                  {accs.map((info) => {
                    const { id, username, social_type, avatar } = info;

                    return (
                      <Box
                        key={id}
                        className="relative"
                        title={username}
                        sx={{
                          position: "relative",
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.8,
                          },
                          "&:hover .close-icon": {
                            display: "flex",
                          },
                        }}>
                        <AvatarIcon
                          avatar={avatar}
                          width="46px"
                          height="46px"
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: "-0.5px",
                            right: "-3px",
                          }}>
                          <IconsAccounts
                            width={22}
                            height={22}
                            socialType={social_type}
                          />
                        </Box>
                        <IconButton
                          className="close-icon"
                          sx={{
                            position: "absolute",
                            top: "3px",
                            right: "3px",
                            display: "none",
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 1)",
                            },
                          }}
                          onClick={() => {
                            this.toggleStateArray("selectedAccounts", id);
                          }}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    );
                  })}
                </List>
              </Box>
              <Box
                className="btnDark"
                onClick={this.handleClickOpen}
                sx={{
                  cursor: isTrialEndedParam ? "not-allowed" : "pointer",
                  opacity: isTrialEndedParam ? 0.5 : 1,
                }}>
                {this.tr("postPage.chooseAccount")}
              </Box>
              {this.getSelectAccsContent()}
            </Box>
          </Box>
        </Box>
        <Box
          className="flex gap-5"
          sx={{ flexDirection: { xs: "column", md: "row" } }}>
          <Box
            className="flex column"
            sx={{
              width: { xs: "100%", md: "66.66%" },
              margin: { xs: "0 5px", md: "0 0 0 40px" },
            }}>
            <Box
              className="content-center"
              sx={{
                backgroundColor: "var(--background-preview)",
                height: "auto",
                borderRadius: "7px",
              }}>
              <Box
                className="mx-5 mb-2 mt-2"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: "bold",
                    color: "var(--text-color)",
                  }}>
                  {this.tr("postPage.captions")}
                </Typography>
                <Box
                  className="btnDark"
                  onClick={() => this.setState({ aiDialogOpen: true })}
                  sx={{
                    cursor: isTrialEndedParam ? "not-allowed" : "pointer",
                    opacity: isTrialEndedParam ? 0.5 : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                  }}>
                  <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                  {this.tr("aiGenerate.button")}
                </Box>
              </Box>
              <Box className="w-full">
                {this.insertGlobalInput({
                  inputRef: (el) => (this.postTitle = el),
                  value: effectiveTitle,
                  onChange: this.handleTitleChange,
                  InputProps: {
                    className: "font f16 mx-5 mb-4",
                    sx: {
                      backgroundColor: "var(--hedear-bg)",
                      color: "var(--input-text)",
                      borderRadius: "7px",
                    },
                  },
                  size: "large",
                  multiline: true,
                  rows: 1,
                })}
              </Box>
              <Box
                className="flex column gap4"
                style={{ position: "relative" }}>
                <Box className="mx-5 mb-2 mt-2">
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: "bold",
                      color: "var(--text-color)",
                    }}>
                    {this.tr("postPage.topic")}
                  </Typography>
                </Box>
                <Box className="w-full">
                  {this.insertGlobalInput({
                    inputRef: (el) => (this.postText = el),
                    value: effectiveText,
                    InputProps: {
                      className: "font f14 mx-5 mb-4",
                      sx: {
                        backgroundColor: "var(--hedear-bg)",
                        color: "var(--input-text)",
                        borderRadius: "7px",
                      },
                    },
                    size: "small",
                    multiline: true,
                    rows: 5,
                    onChange: this.handleTextChange,
                  })}
                </Box>
              </Box>
            </Box>
            <Box
              className="mt-4"
              sx={{
                backgroundColor: "var(--background-preview)",
                minHeight: "150px",
                height: "auto",
                borderRadius: "7px",
              }}>
              <Box className="mx-5 mb-2 mt-2">
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: "bold",
                    color: "var(--text-color)",
                  }}>
                  {this.tr("postPage.tags")}
                </Typography>
              </Box>
              <Box className="relative mx-5 mb-4">
                <TagInput
                  tags={safeTags}
                  onAddTag={this.handleAddTag}
                  text={this.state.postText}
                  onRemoveTag={this.handleRemoveTag}
                />
              </Box>
            </Box>
          </Box>
          <Box
            className="grid"
            sx={{
              width: { xs: "100%", md: "33.33%" },
              alignItems: "center",
              margin: { xs: "0 0 20px 0", md: "0 40px 0 0" },
              backgroundColor: "var(--background-preview)",
              height: "auto",
              borderRadius: "7px",
              overflow: "hidden",
            }}>
            <DateScheduler
              repeatOption={this.state.repeatOption}
              activeDate={this.state.activeDate}
              fromDate={this.state.fromDate}
              toDate={this.state.toDate}
              selectedTime={this.state.selectedTime}
              minToDate={this.state.minToDate}
              onRepeatChange={this.handleChange}
              onActiveDateChange={(activeDate) => this.setState({ activeDate })}
              onDateChange={this.handleDateChange}
              onTimeChange={this.handleTimeChange}
              language={i18n.language}
              tr={this.tr.bind(this)}
            />
          </Box>
        </Box>

        <Box
          className="flex column"
          sx={{
            margin: { xs: "0 5px 40px", md: "0 40px 40px" },
            backgroundColor: "var(--background-preview)",
            borderRadius: "7px",
          }}>
          <Box className="mx-5 mb-2 mt-2">
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: "bold",
                color: "var(--text-color)",
              }}>
              {this.tr("postPage.upload")}
            </Typography>
          </Box>
          <Box
            className={`flex mx-5 mb-2 mt-2`}
            sx={{
              flexWrap: "nowrap",
              gap: "16px",
              borderRadius: "7px",
              padding: "10px",
              cursor: "pointer",
            }}
            onDrop={this.handleDropUpload}
            onDragOver={this.handleDragEnter}
            onDragLeave={this.handleDragLeave}
            onDragEnter={this.handleDragEnter}>
            {this.state.isLoading ? (
              <Box
                sx={{
                  width: "84px",
                  height: "84px",
                  position: "relative",
                  borderRadius: "15px",
                  border: "8px solid #E0E0E0",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                }}>
                <Box
                  sx={{
                    width: "74px",
                    height: "74px",
                    backgroundColor: "#ffffff",
                    opacity: 0.9,
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                  }}>
                  <BrokenImageOutlinedIcon
                    sx={{
                      fontSize: "36px",
                      color: "#A0A0A0",
                    }}
                  />
                </Box>

                <LinearDeterminate
                  uploadTime={totalUploadTime}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: "15px",
                    zIndex: 1,
                    background:
                      "linear-gradient(to right, #42C0C7, #42C0C7 " +
                      totalUploadTime +
                      "%, transparent " +
                      totalUploadTime +
                      "%)",
                  }}
                />
              </Box>
            ) : null}
            {selectedFiles.map((id, i) => (
              <Box
                key={i}
                draggable
                onDragStart={(e) => this.handleImageDragStart(i, e)}
                onDragEnd={this.handleImageDragEnd}
                onDragOver={(e) => this.handleImageDragOverReorder(i, e)}
                sx={{
                  position: "relative",
                  cursor: "grab",
                  opacity: this.state.draggedImageIndex === i ? 0.35 : 1,
                  outline: this.state.dragOverIndex === i && this.state.draggedImageIndex !== null && this.state.draggedImageIndex !== i
                    ? "2px dashed #49B1B5" : "none",
                  borderRadius: "7px",
                  transition: "opacity 0.15s",
                  "&:hover": { opacity: this.state.draggedImageIndex === i ? 0.35 : 0.8 },
                  "&:hover .close-icon": { display: "flex" },
                }}
                className="flex centerV gap8 minH">
                <ImageError
                  src={_request.files.formLink(id)}
                  className="img"
                  width="84px"
                  height="84px"
                  onClick={this.toggleFilesSelection.bind(this, id)}
                />
                <IconButton
                  className="close-icon"
                  sx={{
                    position: "absolute",
                    top: "23px",
                    right: "23px",
                    display: "none",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  }}
                  onClick={() => this.handleFileRemove(id)}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box
              onClick={() => this.setState({ openIconsDialog: true })}
              sx={{
                backgroundColor: "var(--hedear-bg)",
                width: "84px",
                height: "84px",
                borderRadius: "7px",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}>
              <AddIcon
                sx={{
                  color: "#49B1B5",
                  width: "45px",
                  height: "45px",
                }}
              />
            </Box>
            {selectedFiles.length === 0 ? (
              <Box
                className={`drop-area ${
                  this.state.isDragging ? "dragging" : ""
                }`}
                sx={{
                  backgroundColor: "var(--hedear-bg)",
                  width: "85%",
                  height: "84px",
                  borderRadius: "7px",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  border: "2px dashed #49B1B5",
                  transition: "border-color 0.3s",
                  "&:hover": {
                    borderColor: "#3B8B8D",
                  },
                }}>
                <Typography
                  sx={{
                    position: "absolute",
                    fontSize: "12px",
                    color: "var(--text-color)",
                    textAlign: "center",
                  }}>
                  {this.tr("postPage.dragDropFiles")} <br />{" "}
                  {this.tr("postPage.orClickUpload")}
                </Typography>
              </Box>
            ) : null}
            {this.getSelectImage(files, accept)}
          </Box>
        </Box>
      </Box>
    );

    return <Box className="flex1 flex column gap24">{postsBox}</Box>;
  }

  // ─────────────────────────────────────────────────────────────────────────

  handleTabChange(event, newValue) {
    this.setState({ activeTab: newValue });
  }

  handleFileRemove(id) {
    this.setActiveFieldWith("selectedFiles", (prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.filter((fileId) => fileId !== id);
    });
  }

  handleTitleChange = (event) => {
    this.setActiveField("postTitle", event.target.value, { isEditing: true });
  };

  handleTextChange = (event) => {
    this.setActiveField("postText", event.target.value, { isEditing: true });
  };

  handleUserContainerClick = () => {
    const { selectedAccounts } = this.state;

    if (selectedAccounts && selectedAccounts.length) {
      const nextIndex =
        (this.state.selectedIndex + 1) % selectedAccounts.length;
      this.setState({ selectedIndex: nextIndex });
    }
  };
  renderPreview() {
    const { accounts, selectedAccounts } = this.state;

    const safeAccounts = Array.isArray(accounts) ? accounts : [];
    const safeSelectedAccounts = Array.isArray(selectedAccounts) ? selectedAccounts : [];

    // Determine effective preview account (selected tab, or default to first)
    const effectivePreviewId =
      this.state.selectedPreviewAccountId && safeSelectedAccounts.includes(this.state.selectedPreviewAccountId)
        ? this.state.selectedPreviewAccountId
        : safeSelectedAccounts[0] || null;
    const effectivePreviewIndex = effectivePreviewId ? safeSelectedAccounts.indexOf(effectivePreviewId) : 0;

    // Pull the effective per-account content for the previewed account.
    const previewData = effectivePreviewId
      ? getEffectiveData(this.state, effectivePreviewId)
      : getEffectiveData(this.state, null);

    const previewTags = Array.isArray(previewData.tags) ? previewData.tags : [];
    const previewFiles = Array.isArray(previewData.selectedFiles) ? previewData.selectedFiles : [];

    const formattedTags = formatTagsArray(previewTags);

    // The preview media box is ~523 CSS px, so a 300px thumbnail arrives
    // upscaled — ask for one that survives a retina screen (OP#2503).
    const selectedImages = previewFiles.map((id, i) => (
      <ImageError
        key={i}
        src={_request.files.formLink(id, true, THUMB_SIZE.PREVIEW)}
        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "10px" }}
      />
    ));

    // Platform icon-button tabs from selected accounts.
    // A small dot marks accounts whose effective content differs from base;
    // a colored border + status icon marks accounts with validation issues.
    const { accountOverrides } = this.state;
    const validationMap = this.getValidationMap();
    const platformTabs = safeSelectedAccounts.map((id) => {
      const account = safeAccounts.find((acc) => acc && acc.id === id);
      const socialType = account ? account.social_type : "";
      const label = account ? (account.username || socialType) : socialType;
      const isActive = effectivePreviewId === id;
      const overridden = hasOverride(accountOverrides, id);
      const status = issuesStatus(validationMap[id] || []);
      // Pending is not surfaced on preview tabs either — see override-tab note.
      const visibleStatus = status === "pending" ? "ok" : status;
      const statusColor =
        visibleStatus === "error" ? "#e53935" : visibleStatus === "warn" ? "#f5a623" : null;
      const borderColor = isActive
        ? "#42C0C7"
        : statusColor || "var(--border-color)";
      const titleParts = [label];
      if (overridden) titleParts.push(this.tr("postPage.overridden"));
      if (visibleStatus !== "ok") {
        const visibleIssues = (validationMap[id] || []).filter(
          (iss) => iss.severity !== SEVERITY.PENDING,
        );
        for (const iss of visibleIssues) titleParts.push(formatIssue((k) => this.tr(k), iss));
      }
      return (
        <Box
          key={id}
          onClick={() => this.setState({ selectedPreviewAccountId: id })}
          title={titleParts.join("\n")}
          sx={{
            position: "relative",
            cursor: "pointer",
            padding: "4px 10px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: 600,
            // No capitalize here: these labels are social handles, and
            // "libousov_ivan" is not "Libousov_ivan" (OP#2508).
            backgroundColor: isActive ? "#42C0C7" : "transparent",
            color: isActive ? "#fff" : "var(--text-color)",
            border: `1px solid ${borderColor}`,
            opacity: isActive ? 1 : 0.85,
            transition: "all 0.2s",
            "&:hover": { opacity: 1, borderColor: isActive ? "#42C0C7" : statusColor || "#42C0C7" },
          }}>
          {label}
          {visibleStatus !== "ok" && (
            <Box
              component="span"
              sx={{
                marginLeft: "6px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 14,
                height: 14,
                borderRadius: "50%",
                fontSize: "10px",
                fontWeight: 700,
                backgroundColor: isActive ? "rgba(255,255,255,0.25)" : statusColor,
                color: "#fff",
                verticalAlign: "middle",
              }}>
              {visibleStatus === "error" ? "!" : "?"}
            </Box>
          )}
          {overridden && (
            <Box
              component="span"
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: isActive ? "#fff" : "#42C0C7",
              }}
            />
          )}
        </Box>
      );
    });

    return (
      <Box className="post-card post-card-preview">
        <Box className="post-card-header post-card-header-sm">
          <Typography className="post-card-title">
            {this.tr("postPage.preview")}
          </Typography>
        </Box>
        {platformTabs.length > 0 && (
          <Box
            sx={{ display: "flex", flexWrap: "wrap", gap: "8px", padding: "12px 20px 14px" }}>
            {platformTabs}
          </Box>
        )}
        <SocialPreview
          selectedPreviewAccountId={effectivePreviewId}
          selectedAccounts={safeSelectedAccounts}
          accounts={safeAccounts}
          formattedTags={formattedTags}
          selectedImages={selectedImages}
          selectedIndex={effectivePreviewIndex}
          postTitle={previewData.postTitle}
          postText={previewData.postText}
          youtubePrivacy={previewData.youtubePrivacy}
          youtubeCategoryId={previewData.youtubeCategoryId}
          telegramInlineKeyboard={previewData.telegramInlineKeyboard}
          instagramPostType={previewData.instagramPostType}
          threadsReplies={previewData.threadsReplies}
          tr={this.tr.bind(this)}
        />
      </Box>
    );
  }

  renderTopPanel(isTrialEndedParam) {
    const { postID, selectedAccounts } = this.state;
    const isEditing = Boolean(postID);

    // For new posts, validation gates Publish; edit mode is intentionally
    // out of scope for the override/validation feature.
    const hasAccounts = (selectedAccounts || []).length > 0;
    const validationMap = !isEditing && hasAccounts ? this.getValidationMap() : {};
    const blocked = !isEditing && hasAccounts && this.hasBlockingErrors(validationMap);
    const pendingBlocked = !isEditing && hasAccounts && !blocked && this.hasPending(validationMap);
    const errorCount = Object.values(validationMap).reduce(
      (acc, issues) => acc + issues.filter((i) => i.severity === SEVERITY.ERROR).length,
      0,
    );
    const warningCount = !blocked && !pendingBlocked ? this.countWarnings(validationMap) : 0;
    // Disabled if: trial ended, real errors, OR only "missing content" pending.
    const publishDisabled = isTrialEndedParam || blocked || pendingBlocked;
    const publishTitle = blocked
      ? `${errorCount} ${this.tr("postPage.validationBlocked")}`
      : pendingBlocked
        ? this.tr("postPage.completeRequired")
        : warningCount > 0
          ? `${warningCount} ${this.tr("postPage.publishAnyway")}`
          : "";

    return (
      <Box
        className="post-topbar"
        sx={{ backgroundColor: "var(--background-menu)" }}>
        <Typography className="post-topbar-title" sx={{ color: "var(--text-color)" }}>
          {isEditing ? this.tr("postPage.editPost") : this.tr("postPage.post")}
        </Typography>
        <Box className="post-topbar-actions">
          <Box
            className="btnDark post-btn-secondary"
            onClick={
              isTrialEndedParam
                ? null
                : (event) => { this.addDraftShow(event.currentTarget); }
            }
            sx={{ cursor: isTrialEndedParam ? "not-allowed" : "pointer", opacity: isTrialEndedParam ? 0.5 : 1 }}>
            {isEditing ? this.tr("postPage.saveDraft") : this.tr("postPage.postDraft")}
          </Box>
          {this.state.popoverOpen && this.popoverContent()}
          <Box
            className="btnDark post-btn-publish"
            title={publishTitle}
            onClick={
              publishDisabled
                ? null
                : (event) => { this.postNow(event.currentTarget); }
            }
            sx={{
              cursor: publishDisabled ? "not-allowed" : "pointer",
              opacity: publishDisabled ? 0.5 : 1,
            }}>
            {isEditing ? this.tr("postPage.updatePost") : this.tr("postPage.publish")}
            {(blocked && errorCount > 0) || warningCount > 0 ? (
              <Box
                component="span"
                sx={{
                  marginLeft: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 18,
                  height: 18,
                  padding: "0 5px",
                  borderRadius: "9px",
                  fontSize: "11px",
                  fontWeight: 700,
                  backgroundColor: blocked ? "#e53935" : "#f5a623",
                  color: "#fff",
                }}>
                {blocked ? errorCount : warningCount}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  }

  // Renders a 3-section panel: errors (red), warnings (amber),
  // pending/required (neutral gray). Empty sections are skipped, and the
  // whole panel is omitted when there are no issues at all.
  // `issues` here may include {accountId} when in base mode (cross-account).
  renderIssuesPanel(issues, accs) {
    if (!Array.isArray(issues) || issues.length === 0) return null;

    const errors = issues.filter((i) => i.severity === SEVERITY.ERROR);
    const warns = issues.filter((i) => i.severity === SEVERITY.WARN);
    const pending = issues.filter((i) => i.severity === SEVERITY.PENDING);

    const labelFor = (iss) => {
      if (!iss.accountId) return "";
      const a = (accs || []).find((x) => x.id === iss.accountId);
      return a ? `${a.username || a.social_type}: ` : "";
    };

    const Section = ({ items, color, bg, border, icon, title }) => {
      if (!items || items.length === 0) return null;
      return (
        <Box
          sx={{
            margin: "8px 20px 0",
            padding: "10px 12px",
            borderRadius: "8px",
            backgroundColor: bg,
            border: `1px solid ${border}`,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}>
          {title && (
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "2px",
              }}>
              {title}
            </Typography>
          )}
          {items.map((iss, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "6px",
                fontSize: "12px",
                color,
              }}>
              <span style={{ fontWeight: 700 }}>{icon}</span>
              <span>
                {labelFor(iss)}
                {formatIssue((k) => this.tr(k), iss)}
              </span>
            </Box>
          ))}
        </Box>
      );
    };

    return (
      <>
        <Section
          items={errors}
          color="#e53935"
          bg="rgba(229,57,53,0.08)"
          border="rgba(229,57,53,0.35)"
          icon="✕"
        />
        <Section
          items={warns}
          color="#f5a623"
          bg="rgba(245,166,35,0.08)"
          border="rgba(245,166,35,0.35)"
          icon="⚠"
        />
        <Section
          items={pending}
          color="var(--text-color)"
          bg="rgba(255,255,255,0.04)"
          border="rgba(255,255,255,0.12)"
          icon="○"
          title={this.tr("postPage.requiredToPublish")}
        />
      </>
    );
  }

  // Lists all warning issues across selected accounts and lets the user
  // confirm publication despite them. Errors are handled separately and
  // never reach this dialog.
  renderPublishWarningDialog() {
    const { confirmPublishOpen, accounts, selectedAccounts } = this.state;
    if (!confirmPublishOpen) return null;
    const safeAccounts = Array.isArray(accounts) ? accounts : [];
    const safeSelected = Array.isArray(selectedAccounts) ? selectedAccounts : [];
    const map = this.getValidationMap();
    const items = [];
    for (const id of safeSelected) {
      const acc = safeAccounts.find((a) => a && a.id === id);
      const warnings = (map[id] || []).filter((iss) => iss.severity === SEVERITY.WARN);
      for (const iss of warnings) {
        items.push({
          accountLabel: acc ? acc.username || acc.social_type : id,
          message: formatIssue((k) => this.tr(k), iss),
        });
      }
    }

    const close = () => this.setState({ confirmPublishOpen: false });

    return (
      <Dialog
        open
        onClose={close}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "var(--background-menu)",
            color: "var(--text-color)",
            borderRadius: "10px",
            minWidth: "420px",
            maxWidth: "600px",
          },
        }}>
        <DialogTitle sx={{ color: "var(--text-color)", fontWeight: 700 }}>
          {this.tr("postPage.publishAnyway")}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.12)" }}>
          <Typography sx={{ fontSize: "13px", marginBottom: "12px", color: "var(--text-color)", opacity: 0.85 }}>
            {this.tr("postPage.publishAnywayDescription")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {items.map((it, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  fontSize: "12px",
                  color: "#f5a623",
                }}>
                <span style={{ fontWeight: 700 }}>⚠</span>
                <span>
                  <strong style={{ marginRight: 4 }}>{it.accountLabel}:</strong>
                  {it.message}
                </span>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} sx={{ color: "var(--text-color)" }}>
            {this.tr("cancel")}
          </Button>
          <Button
            onClick={() => {
              close();
              this._doPostNow();
            }}
            sx={{
              backgroundColor: "#42C0C7",
              color: "#fff",
              "&:hover": { backgroundColor: "#38a8ae" },
            }}>
            {this.tr("postPage.publishAnywayConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderPage() {
    const {
      alertOpen,
      alertMessage,
      alertErrorOpen,
      metaData,
      isUnsavedDialogOpen,
      snackbarOpen,
      snackbarMessage,
    } = this.state;

    const isTrialEndedValue = isTrialEnded(metaData);

    return (
      <Box className="post-page-v2">
        {/* ── Top bar ── */}
        {this.renderTopPanel(isTrialEndedValue)}

        {/* ── Mobile tabs ── */}
        <Box sx={{ display: { md: "none" }, padding: "0 16px", backgroundColor: "var(--background-menu)", borderBottom: "1px solid var(--border-color, #ffffff14)" }}>
          <Tabs
            value={this.state.activeTab}
            onChange={this.handleTabChange}
            sx={{
              minHeight: 44,
              "& .MuiTab-root": { color: "var(--text-color)", fontWeight: 600, fontSize: "13px", minHeight: 44, textTransform: "none" },
              "& .Mui-selected": { color: "#42C0C7 !important" },
              "& .MuiTabs-indicator": { backgroundColor: "#42C0C7" },
            }}>
            <Tab label={this.tr("postPage.compose")}/>
            <Tab label={this.tr("postPage.preview")}/>
          </Tabs>
        </Box>

        {/* ── Main 2-column layout ── */}
        <Box className="post-page-body">
          {/* Left column: compose + tags */}
          <Box
            className="post-col-left"
            sx={{ display: { xs: this.state.activeTab === 0 ? "flex" : "none", md: "flex" } }}>
            {this.renderComposeCard(isTrialEndedValue)}
            {this.renderYouTubeSettingsCard()}
            {this.renderTelegramSettingsCard()}
            {this.renderInstagramSettingsCard()}
            {this.renderTikTokSettingsCard()}
            {this.renderThreadsThreadCard()}
            {this.renderTagsCard()}
          </Box>

          {/* Right column: preview + schedule */}
          <Box
            className="post-col-right"
            sx={{ display: { xs: this.state.activeTab === 1 ? "flex" : "none", md: "flex" } }}>
            {this.renderPreview()}
            {this.renderScheduleCard()}
          </Box>
        </Box>

        {/* ── Trial ended overlay ── */}
        {isTrialEndedValue && (
          <Box className="post-trial-overlay">
            <Box className="post-trial-box">
              <Typography variant="h6" sx={{ color: "error.main", fontWeight: 700, mb: 1 }}>
                {this.tr("postPage.trialEnded")}
              </Typography>
              <Link to="/app/pricing">
                <Button variant="contained" sx={{ backgroundColor: "#42C0C7", "&:hover": { backgroundColor: "#38a8ae" } }}>
                  {this.tr("postPage.upgradeNow")}
                </Button>
              </Link>
            </Box>
          </Box>
        )}

        {/* ── Snackbar ── */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={this.handleSnackbarClose}
          message={snackbarMessage}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          action={
            <Button color="secondary" size="small" onClick={this.handleSnackbarClose}>
              {this.tr("postPage.close")}
            </Button>
          }
        />

        {/* ── LinkedIn reconnect snackbar (LI_TOKEN_INVALID) ── */}
        <Snackbar
          open={!!this.state.linkedinReconnectOpen}
          onClose={this.closeLinkedinReconnect}
          message={this.tr("postPage.linkedinSessionExpired")}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          action={
            <Box sx={{ display: "flex", gap: "4px" }}>
              <Button color="primary" size="small" onClick={this.handleLinkedinReconnect}>
                {this.tr("postPage.linkedinReconnect")}
              </Button>
              <Button color="inherit" size="small" onClick={this.closeLinkedinReconnect}>
                {this.tr("postPage.close")}
              </Button>
            </Box>
          }
        />

        {/* ── Alerts ── */}
        {alertOpen && <SuccessAlert message={alertMessage}>{alertMessage}</SuccessAlert>}
        {alertErrorOpen && <ErrorAlert message={alertMessage}>{alertMessage}</ErrorAlert>}
        {isUnsavedDialogOpen && <ConfirmDialog message="You have unsaved changes. Are you sure you want to leave?" />}

        {/* ── Publish-with-warnings confirmation ── */}
        {this.renderPublishWarningDialog()}

        <AIGenerateDialog
          open={this.state.aiDialogOpen}
          onClose={() => this.setState({ aiDialogOpen: false })}
          onApply={this.handleAIApply}
          tr={this.tr.bind(this)}
          selectedAccounts={this.state.selectedAccounts}
          accounts={this.state.accounts}
        />

        <PublishStatusDialog
          open={this.state.publishStatusOpen}
          postIds={this.state.publishStatusPostIds}
          accounts={this.state.accounts}
          onClose={() =>
            this.setState({ publishStatusOpen: false, publishStatusPostIds: [] })
          }
          tr={this.tr.bind(this)}
          onTokenFailures={this.handlePublishTokenFailures}
          onReconnect={this.handleReconnectFromTask}
        />
      </Box>
    );
  }

  handleAIApply = ({ text, title, hashtags }) => {
    if (text) {
      this.setActiveField("postText", text, { isEditing: true });
    }
    if (title) {
      this.setActiveField("postTitle", title);
    }
    if (hashtags?.length) {
      this.setActiveFieldWith("tags", (prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        const cleaned = hashtags.map((t) => t.replace(/^#/, ""));
        return [...existing, ...cleaned.filter((t) => !existing.includes(t))];
      });
    }
  };

  tr(key, options = {}) {
    return i18n.t(key, options);
  }
}

export default function Post() {
  const location = useLocation();
  return <PostPage {...useOutletContext()} location={location} />;
}

